import type { ModalProps } from 'antd';
import { ConfigProvider, Modal, Typography } from 'antd';
import { createStyles } from 'antd-style';
import type { ReactNode } from 'react';

const useStyles = createStyles(({ token, css }) => ({
  titleWrap: css`
    margin-bottom: ${token.marginLG}px;
  `,
  title: css`
    && {
      margin-bottom: ${token.marginXXS}px;
      font-weight: 600;
    }
  `,
  subtitle: css`
    color: ${token.colorTextSecondary};
    font-size: 13px;
    line-height: 1.6;
    display: block;
  `,
}));

export interface FormModalProps
  extends Omit<
    ModalProps,
    'title' | 'children' | 'okButtonProps' | 'cancelButtonProps'
  > {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  tone?: 'primary' | 'danger';
  okButtonProps?: ModalProps['okButtonProps'];
  cancelButtonProps?: ModalProps['cancelButtonProps'];
}

const FormModal: React.FC<FormModalProps> = ({
  title,
  subtitle,
  children,
  tone = 'primary',
  width = 580,
  centered = true,
  maskClosable = false,
  destroyOnHidden = true,
  okButtonProps,
  cancelButtonProps,
  ...rest
}) => {
  const { styles } = useStyles();

  return (
    <Modal
      title={null}
      width={width}
      centered={centered}
      maskClosable={maskClosable}
      destroyOnHidden={destroyOnHidden}
      okButtonProps={{
        size: 'large',
        type: 'primary',
        danger: tone === 'danger',
        ...okButtonProps,
      }}
      cancelButtonProps={{
        size: 'large',
        ...cancelButtonProps,
      }}
      {...rest}
    >
      <div className={styles.titleWrap}>
        <Typography.Title level={4} className={styles.title}>
          {title}
        </Typography.Title>
        {subtitle && (
          <Typography.Text className={styles.subtitle}>
            {subtitle}
          </Typography.Text>
        )}
      </div>
      <ConfigProvider componentSize="large">{children}</ConfigProvider>
    </Modal>
  );
};

export default FormModal;
